import React, { useState, useEffect } from 'react'
import { apiService } from '@templatean/shared/services'
import { outlookService } from '../services/outlookService'

interface QuickResponse {
  id: string
  name: string
  content: string
  category: string
  shortcut?: string
}

const QuickResponses: React.FC = () => {
  const [responses, setResponses] = useState<QuickResponse[]>([])
  const [categories, setCategories] = useState<string[]>(['All'])
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [newResponse, setNewResponse] = useState({
    name: '',
    content: '',
    category: '',
    shortcut: ''
  })

  useEffect(() => {
    loadQuickResponses()
  }, [])

  const loadQuickResponses = async () => {
    try {
      const response = await apiService.get<QuickResponse[]>('/quick-responses')
      setResponses(response.data)

      // Extract unique categories
      const uniqueCategories: string[] = ['All', ...new Set(response.data.map((r: QuickResponse) => r.category))]
      setCategories(uniqueCategories)
    } catch (error) {
      console.error('Error loading quick responses:', error)
    }
  }

  const filteredResponses = responses.filter(response => {
    const matchesCategory = selectedCategory === 'All' || response.category === selectedCategory
    const matchesSearch = response.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         response.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (response.shortcut && response.shortcut.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesCategory && matchesSearch
  })

  const handleInsertResponse = async (response: QuickResponse) => {
    try {
      await outlookService.insertText(response.content)
      alert('Response inserted!')
    } catch (error) {
      console.error('Error inserting response:', error)
      alert('Failed to insert response')
    }
  }

  const handleCreateResponse = async () => {
    if (!newResponse.name || !newResponse.content || !newResponse.category) {
      alert('Please fill in all required fields')
      return
    }

    try {
      await apiService.post('/quick-responses', newResponse)
      setIsCreating(false)
      setNewResponse({ name: '', content: '', category: '', shortcut: '' })
      loadQuickResponses()
    } catch (error) {
      console.error('Error creating response:', error)
      alert('Failed to create response')
    }
  }

  const handleDeleteResponse = async (id: string) => {
    if (!confirm('Are you sure you want to delete this quick response?')) {
      return
    }

    try {
      await apiService.delete(`/quick-responses/${id}`)
      loadQuickResponses()
    } catch (error) {
      console.error('Error deleting response:', error)
      alert('Failed to delete response')
    }
  }

  return (
    <div className="quick-responses">
      <div className="responses-header">
        <h3>Quick Responses</h3>
        <button 
          onClick={() => setIsCreating(true)}
          className="btn btn-primary btn-sm"
        >
          + New Response
        </button>
      </div>

      <div className="responses-controls">
        <input
          type="text"
          placeholder="Search responses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-filter"
        >
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      <div className="responses-list">
        {filteredResponses.map(response => (
          <div key={response.id} className="response-item">
            <div className="response-header">
              <h4>{response.name}</h4>
              {response.shortcut && (
                <span className="shortcut-badge">/{response.shortcut}</span>
              )}
            </div>
            
            <p className="response-content">{response.content}</p>
            
            <div className="response-footer">
              <span className="response-category">{response.category}</span>
              <div className="response-actions">
                <button
                  onClick={() => handleInsertResponse(response)}
                  className="btn btn-sm btn-primary"
                >
                  Insert
                </button>
                <button
                  onClick={() => handleDeleteResponse(response.id)}
                  className="btn btn-sm btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isCreating && (
        <div className="create-response-modal" onClick={() => setIsCreating(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create Quick Response</h3>
            
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={newResponse.name}
                onChange={(e) => setNewResponse({ ...newResponse, name: e.target.value })}
                placeholder="e.g., Meeting Confirmation"
              />
            </div>

            <div className="form-group">
              <label>Content *</label>
              <textarea
                value={newResponse.content}
                onChange={(e) => setNewResponse({ ...newResponse, content: e.target.value })}
                placeholder="Type your response content..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Category *</label>
              <input
                type="text"
                value={newResponse.category}
                onChange={(e) => setNewResponse({ ...newResponse, category: e.target.value })}
                placeholder="e.g., Meetings, Follow-ups"
              />
            </div>

            <div className="form-group">
              <label>Shortcut (optional)</label>
              <input
                type="text"
                value={newResponse.shortcut}
                onChange={(e) => setNewResponse({ ...newResponse, shortcut: e.target.value })}
                placeholder="e.g., mtg"
              />
            </div>

            <div className="modal-actions">
              <button onClick={handleCreateResponse} className="btn btn-primary">
                Create
              </button>
              <button onClick={() => setIsCreating(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuickResponses